import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Textarea } from "@/components/ui/Input";
import { cn } from "@/lib/cn";
import { getContentFilter, putContentFilter } from "./api";
import { AdminLoadState, isAdminForbidden } from "./AdminLoadState";
import {
  ADMIN_FORBIDDEN_MESSAGE,
  CONTENT_FILTER_MAX_TERMS,
  CONTENT_FILTER_TERM_MAX_RAW,
  CONTENT_FILTER_TOTAL_MAX,
} from "./types";
import { parseContentFilterTerms, validateContentFilterTerms } from "./validation";
import { useUgcAdminScope } from "./useCanManageUgc";

export function ContentFilterAdmin(): JSX.Element {
  const scope = useUgcAdminScope();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["admin-content-filter", scope?.userId, scope?.tenantId],
    refetchOnWindowFocus: false,
    retry: false,
    enabled: !!scope,
    queryFn: getContentFilter,
  });
  const [enabled, setEnabled] = useState(false);
  const [rawTerms, setRawTerms] = useState("");
  const [hydrated, setHydrated] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [accessRevoked, setAccessRevoked] = useState(false);
  const scopeKey = `${scope?.userId ?? ""}:${scope?.tenantId ?? ""}`;
  const [seenScope, setSeenScope] = useState(scopeKey);

  const resetForm = () => {
    setEnabled(false);
    setRawTerms("");
    setHydrated(false);
    setError(null);
    setSuccess(null);
  };

  if (seenScope !== scopeKey) {
    setSeenScope(scopeKey);
    setEnabled(false);
    setRawTerms("");
    setHydrated(false);
    setError(null);
    setSuccess(null);
    setAccessRevoked(false);
  }

  useEffect(() => {
    if (!query.data || isAdminForbidden(query.error)) {
      if (!query.data) {
        setEnabled(false);
        setRawTerms("");
        setHydrated(false);
      }
      return;
    }
    setEnabled(query.data.enabled);
    setRawTerms(query.data.terms.join("\n"));
    setHydrated(true);
  }, [query.data, query.error]);

  useEffect(() => {
    if (isAdminForbidden(query.error)) {
      setAccessRevoked(true);
      resetForm();
    }
  }, [query.error]);

  const terms = useMemo(() => parseContentFilterTerms(rawTerms), [rawTerms]);
  const issues = useMemo(() => validateContentFilterTerms(terms), [terms]);
  const forbidden = accessRevoked || isAdminForbidden(query.error);
  const showForm = hydrated && !!query.data && !forbidden;

  const save = async () => {
    if (!showForm || busy || issues.length > 0 || forbidden) return;
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const saved = await putContentFilter({ enabled, terms });
      setEnabled(saved.enabled);
      setRawTerms(saved.terms.join("\n"));
      setSuccess("コンテンツフィルターを保存しました。");
      await query.refetch();
    } catch (e) {
      setSuccess(null);
      if (isAdminForbidden(e)) {
        setAccessRevoked(true);
        resetForm();
        queryClient.removeQueries({ queryKey: ["admin-content-filter"] });
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <header className="mb-6 border-b border-border-subtle pb-4">
        <h1 className="text-xl font-semibold text-fg-primary">コンテンツフィルター</h1>
        <p className="mt-1 text-sm text-fg-secondary">
          登録した語句を含む新しい投稿と編集を拒否します。
        </p>
      </header>

      {query.isPending && !forbidden && (
        <p role="status" className="text-sm text-fg-secondary">
          フィルター設定を読み込んでいます…
        </p>
      )}
      {(query.error || forbidden) && (
        <AdminLoadState
          error={query.error ?? new ApiError(403, ADMIN_FORBIDDEN_MESSAGE)}
          onRetry={() => {
            setAccessRevoked(false);
            void query.refetch();
          }}
        />
      )}

      {showForm && (
        <section className="mb-4 overflow-hidden rounded-lg bg-canvas shadow-sm ring-1 ring-border-subtle">
          <header className="border-b border-border-subtle px-5 py-3">
            <h2 className="text-sm font-semibold text-fg-primary">登録する語句</h2>
            <p className="mt-0.5 text-xs text-fg-muted">
              全角半角・空白の違いをそろえて照合。引用にも適用。画像や意味の判定は対象外
            </p>
          </header>
          <div className="space-y-4 p-5">
            <div className="flex items-center justify-between gap-3 rounded-md border border-border-subtle bg-surface-base px-4 py-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-fg-primary">フィルターを有効にする</p>
                <p className="text-xs text-fg-muted">保存後の新しい投稿と編集に適用します。</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={enabled}
                aria-label="フィルターを有効にする"
                disabled={busy}
                onClick={() => setEnabled((value) => !value)}
                className={cn(
                  "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus-visible:shadow-focus",
                  enabled ? "bg-brand-500" : "bg-fg-subtle",
                )}
              >
                <span
                  className={cn(
                    "inline-block h-4 w-4 transform rounded-full bg-canvas shadow transition-transform",
                    enabled ? "translate-x-6" : "translate-x-1",
                  )}
                />
              </button>
            </div>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-fg-secondary">禁止語句（1行1件）</span>
              <Textarea
                aria-label="禁止語句"
                rows={10}
                value={rawTerms}
                disabled={busy}
                onChange={(e) => {
                  setRawTerms(e.target.value);
                  setSuccess(null);
                }}
                className="font-mono text-xs"
              />
              <span className="text-xs text-fg-muted">
                {terms.length} / {CONTENT_FILTER_MAX_TERMS} 件 · 各 {CONTENT_FILTER_TERM_MAX_RAW} 文字まで · 合計{" "}
                {terms.reduce((sum, term) => sum + term.length, 0)} / {CONTENT_FILTER_TOTAL_MAX} 文字
              </span>
            </label>
            {issues.length > 0 && (
              <ul role="alert" className="space-y-1 text-xs text-danger">
                {issues.map((issue) => (
                  <li key={issue}>{issue}</li>
                ))}
              </ul>
            )}
            {busy && (
              <p role="status" className="text-sm text-fg-secondary">
                処理中…
              </p>
            )}
            {error && (
              <p role="alert" className="text-sm text-danger">
                {error}
              </p>
            )}
            {success && <p role="status">{success}</p>}
            <Button onClick={() => void save()} disabled={busy || issues.length > 0}>
              保存
            </Button>
          </div>
        </section>
      )}
    </>
  );
}
